import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Tag, Kanban, FolderOpen, FlagBanner, Circle, CircleDashed, CircleHalf, CheckCircle as CheckCircleIcon, XCircle } from '@phosphor-icons/react';
import { entityService } from '../../services/entityService';
import { getTasksAppStore } from '../../apps/TasksApp/store/tasksAppStore';
import DatePicker from '../../apps/TasksApp/components/DatePicker';
import FieldDropdown, { DropdownOption } from '../../apps/TasksApp/components/FieldDropdown';
import TagDropdown from '../../apps/TasksApp/components/TagDropdown';
import { TaskStatus } from '../../apps/TasksApp/types';
import '../../apps/TasksApp/TasksApp.scss'; // Import TasksApp styles for classes
import './GlobalQuickTaskModal.scss';

interface GlobalQuickTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * GlobalQuickTaskModal - Lightweight task capture from anywhere in RiverOS
 * 
 * Triggered by: Cmd+Shift+T globally
 * Styled exactly like expanded task in TasksApp
 * 
 * Features:
 * - Quick task title entry
 * - Creates task in default space
 * - Closes automatically after creation
 * - Works even when TasksApp isn't open
 * 
 * @props {isOpen, onClose} - Modal state control
 * @sideEffects Creates Task entity in default space
 */
const GlobalQuickTaskModal: React.FC<GlobalQuickTaskModalProps> = ({ isOpen, onClose }) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState<TaskStatus>('todo');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [projectId, setProjectId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [openField, setOpenField] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  
  // Use refs for drag performance (avoid re-renders during drag)
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartModalPos = useRef({ x: 0, y: 0 });

  const titleInputRef = useRef<HTMLTextAreaElement>(null);
  const notesInputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get default space store for projects/areas/tasks list
  const spaceId = localStorage.getItem('currentSpaceId') || 'default';
  const store = getTasksAppStore('global-quick-task', spaceId);
  const { projects, areas, tasks } = store();

  // Center modal on mount
  useEffect(() => {
    if (isOpen) {
      const centerX = window.innerWidth / 2 - 300; // 300 = half of modal width (600px)
      const centerY = window.innerHeight / 2 - 150;
      setModalPosition({ x: Math.max(20, centerX), y: Math.max(20, centerY) });
    }
  }, [isOpen]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      const storeState = store.getState();
      if (!storeState.isLoading && tasks.length === 0) {
        storeState.initialize('global-quick-task', spaceId);
      }
    }
  }, [isOpen]);

  // Auto-focus title when modal opens
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isOpen]);

  // Auto-resize title textarea
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.style.height = 'auto';
      titleInputRef.current.style.height = titleInputRef.current.scrollHeight + 'px';
    }
  }, [title]);

  // Auto-resize notes textarea
  useEffect(() => {
    if (notesInputRef.current) {
      notesInputRef.current.style.height = 'auto';
      notesInputRef.current.style.height = notesInputRef.current.scrollHeight + 'px';
    }
  }, [notes]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !openField) {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, openField, onClose]);

  // Dragging handlers - use direct DOM manipulation for smooth 60fps
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't start drag if clicking on input fields or buttons
    const target = e.target as HTMLElement;
    if (
      target.closest('textarea') ||
      target.closest('button') ||
      target.closest('.tasksFieldButtonWrapper') ||
      target.closest('.tasksDatePicker')
    ) {
      return;
    }
    
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    dragStartModalPos.current = modalPosition;
  }, [modalPosition]);

  // Handle dragging - update DOM directly, no state updates during drag
  useEffect(() => {
    if (!isDragging || !containerRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;
      
      const newX = dragStartModalPos.current.x + deltaX;
      const newY = dragStartModalPos.current.y + deltaY;
      
      // Update DOM directly (no React re-render = smooth!)
      if (containerRef.current) {
        containerRef.current.style.left = `${newX}px`;
        containerRef.current.style.top = `${newY}px`;
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      
      // Save final position to state
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setModalPosition({ x: rect.left, y: rect.top });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Click outside to save and close (but not when dragging)
  useEffect(() => {
    if (!isOpen || isDragging) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleSaveAndClose();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isDragging, title, notes, tags, dueDate, status, priority, projectId, areaId]);

  const handleSaveAndClose = async () => {
    if (!title.trim()) {
      onClose();
      return;
    }

    if (isCreating) return;
    setIsCreating(true);

    try {
      await entityService.create({
        entityType: 'Task',
        spaceId: spaceId,
        name: title.trim(),
        hidden: true,
        skeleton: {
          '@type': 'Task',
          notes: notes,
          status: status,
          completed: status === 'done',
          completedAt: status === 'done' ? new Date().toISOString() : undefined,
          tags: tags,
          dueDate: dueDate || undefined,
          priority: priority,
          projectId: projectId || undefined,
          areaId: areaId || undefined,
          isInToday: false,
          order: Date.now()
        }
      });

      // Reset and close
      setTitle('');
      setNotes('');
      setTags([]);
      setDueDate('');
      setStatus('todo');
      setPriority('medium');
      setProjectId('');
      setAreaId('');
      setOpenField(null);
      onClose();
    } catch (error) {
      console.error('Failed to create task:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const toggleField = (field: string) => {
    setOpenField(openField === field ? null : field);
  };

  // Get all unique tags for autocomplete
  const allTags = Array.from(new Set(tasks.flatMap(t => t.skeleton?.tags || []))).sort();

  // Status options (EXACTLY like TaskItem)
  const statusOptions: DropdownOption[] = [
    { value: 'backlog', label: 'Backlog', icon: <CircleDashed size={14} weight="bold" />, shortcut: '1' },
    { value: 'todo', label: 'Todo', icon: <Circle size={14} weight="bold" />, shortcut: '2' },
    { value: 'in-progress', label: 'In Progress', icon: <CircleHalf size={14} weight="bold" />, shortcut: '3' },
    { value: 'done', label: 'Done', icon: <CheckCircleIcon size={14} weight="bold" />, shortcut: '4' },
    { value: 'canceled', label: 'Canceled', icon: <XCircle size={14} weight="bold" />, shortcut: '5' }
  ];

  // Priority options
  const priorityOptions: DropdownOption[] = [
    { value: 'low', label: 'Low', icon: '▃', shortcut: '4' },
    { value: 'medium', label: 'Medium', icon: '▄', shortcut: '3' },
    { value: 'high', label: 'High', icon: '▆', shortcut: '2' }
  ];

  // Project options
  const projectOptions: DropdownOption[] = projects.map(p => ({
    value: p._id,
    label: p.name,
    icon: '📦'
  }));

  // Area options
  const areaOptions: DropdownOption[] = areas.map(a => ({
    value: a._id,
    label: a.name,
    icon: '🏷️'
  }));

  const getStatusIcon = (s: TaskStatus) => {
    switch (s) {
      case 'backlog': return <CircleDashed size={14} weight="bold" />;
      case 'todo': return <Circle size={14} weight="bold" />;
      case 'in-progress': return <CircleHalf size={14} weight="bold" />;
      case 'done': return <CheckCircleIcon size={14} weight="bold" />;
      case 'canceled': return <XCircle size={14} weight="bold" />;
      default: return <Circle size={14} weight="bold" />;
    }
  };

  const getStatusLabel = (s: TaskStatus) => {
    switch (s) {
      case 'backlog': return 'Backlog';
      case 'todo': return 'Todo';
      case 'in-progress': return 'In Progress';
      case 'done': return 'Done';
      case 'canceled': return 'Canceled';
      default: return 'Todo';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        ref={containerRef}
        className={`tasksItem tasksItemExpanded globalQuickTaskFloating ${isDragging ? 'globalQuickTaskDragging' : ''}`}
        style={{
          position: 'fixed',
          left: `${modalPosition.x}px`,
          top: `${modalPosition.y}px`,
          zIndex: 9999,
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Checkbox */}
        <div className="tasksItemCheckbox">
          <button
            className={`tasksCheckbox ${status === 'done' ? 'tasksCheckboxChecked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setStatus(status === 'done' ? 'todo' : 'done');
            }}
            title="Mark complete"
          >
            {status === 'done' && <span className="tasksCheckboxIcon">✓</span>}
          </button>
        </div>

        {/* Content - EXACTLY like expanded task */}
        <div className="tasksItemContent">
          <div className="tasksItemEditForm">
            {/* Close button - positioned absolutely */}
            <button
              className="globalQuickTaskClose"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveAndClose();
              }}
              title="Close"
            >
              <X size={16} weight="bold" />
            </button>

            {/* Title input */}
            <textarea
              ref={titleInputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="tasksItemTitleInput"
              placeholder="Task name"
              rows={1}
              disabled={isCreating}
            />

            {/* Expanded fields */}
            <div className="tasksItemExpandedFields">
              {/* Notes field */}
              <textarea
                ref={notesInputRef}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes"
                className="tasksItemNotesInput"
                rows={1}
                disabled={isCreating}
              />

              {/* Field buttons - EXACTLY like TaskItem */}
              <div className="tasksItemFieldButtons">
                {/* Status */}
                <div className="tasksFieldButtonWrapper">
                  <button
                    type="button"
                    className={`tasksFieldButton ${openField === 'status' ? 'tasksFieldButtonActive' : ''}`}
                    onClick={() => toggleField('status')}
                  >
                    {getStatusIcon(status)}
                    <span>{getStatusLabel(status)}</span>
                  </button>
                  {openField === 'status' && (
                    <FieldDropdown
                      options={statusOptions}
                      value={status}
                      onChange={(val) => {
                        setStatus(val as TaskStatus);
                        setOpenField(null);
                      }}
                      onClose={() => setOpenField(null)}
                      placeholder="Status"
                      searchPlaceholder="Change status..."
                      allowClear={false}
                    />
                  )}
                </div>

                {/* Date picker */}
                <DatePicker
                  value={dueDate}
                  onChange={setDueDate}
                  onClear={() => setDueDate('')}
                />

                {/* Tags */}
                <div className="tasksFieldButtonWrapper">
                  <button
                    type="button"
                    className={`tasksFieldButton ${openField === 'tags' || tags.length > 0 ? 'tasksFieldButtonActive' : ''}`}
                    onClick={() => toggleField('tags')}
                  >
                    <Tag size={14} weight="bold" />
                    <span>{tags.length > 0 ? `${tags.length} tag${tags.length > 1 ? 's' : ''}` : 'Tags'}</span>
                  </button>
                  {openField === 'tags' && (
                    <TagDropdown
                      selectedTags={tags}
                      availableTags={allTags}
                      onChange={setTags}
                      onClose={() => setOpenField(null)}
                    />
                  )}
                </div>

                {/* Priority */}
                <div className="tasksFieldButtonWrapper">
                  <button
                    type="button"
                    className={`tasksFieldButton ${openField === 'priority' || priority !== 'medium' ? 'tasksFieldButtonActive' : ''}`}
                    onClick={() => toggleField('priority')}
                  >
                    <FlagBanner size={14} weight="bold" />
                    <span>{priority === 'medium' ? 'Priority' : priority.charAt(0).toUpperCase() + priority.slice(1)}</span>
                  </button>
                  {openField === 'priority' && (
                    <FieldDropdown
                      options={priorityOptions}
                      value={priority}
                      onChange={(val) => {
                        setPriority(val as 'low' | 'medium' | 'high');
                        setOpenField(null);
                      }}
                      onClose={() => setOpenField(null)}
                      placeholder="Priority"
                      searchPlaceholder="Set priority to..."
                      allowClear={false}
                    />
                  )}
                </div>

                {/* Project */}
                <div className="tasksFieldButtonWrapper">
                  <button
                    type="button"
                    className={`tasksFieldButton ${openField === 'project' || projectId ? 'tasksFieldButtonActive' : ''}`}
                    onClick={() => toggleField('project')}
                  >
                    <Kanban size={14} weight="bold" />
                    <span>{projectId ? projects.find(p => p._id === projectId)?.name : 'Project'}</span>
                  </button>
                  {openField === 'project' && (
                    <FieldDropdown
                      options={projectOptions}
                      value={projectId}
                      onChange={(val) => {
                        setProjectId(val);
                        setOpenField(null);
                      }}
                      onClose={() => setOpenField(null)}
                      placeholder="Project"
                      searchPlaceholder="Select project..."
                      emptyMessage="No projects"
                    />
                  )}
                </div>

                {/* Area */}
                <div className="tasksFieldButtonWrapper">
                  <button
                    type="button"
                    className={`tasksFieldButton ${openField === 'area' || areaId ? 'tasksFieldButtonActive' : ''}`}
                    onClick={() => toggleField('area')}
                  >
                    <FolderOpen size={14} weight="bold" />
                    <span>{areaId ? areas.find(a => a._id === areaId)?.name : 'Area'}</span>
                  </button>
                  {openField === 'area' && (
                    <FieldDropdown
                      options={areaOptions}
                      value={areaId}
                      onChange={(val) => {
                        setAreaId(val);
                        setOpenField(null);
                      }}
                      onClose={() => setOpenField(null)}
                      placeholder="Area"
                      searchPlaceholder="Select area..."
                      emptyMessage="No areas"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalQuickTaskModal;

